import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid';

const RequisicionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [areas, setAreas] = useState([]);
  const [area, setArea] = useState('');
  const [estado, setEstado] = useState('Pendiente');
  const [moneda, setMoneda] = useState('MXN'); // Global currency for all articles
  const [detalles, setDetalles] = useState([
    { id: uuidv4(), articulo: '', cantidad: '1', unidad: '', precio: '' },
  ]);
  const [impuestos, setImpuestos] = useState([]);
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState('');
  const [editableTotal, setEditableTotal] = useState('');

  useEffect(() => {
    const fetchEmpresas = async () => {
      try {
        const response = await axios.get('/api/empresas');
        setEmpresas(response.data);
      } catch (err) {
        setError('No se pudieron cargar las empresas.');
        console.error(err);
      }
    };

    fetchEmpresas();

    if (isEditMode) {
      const fetchRequisicion = async () => {
        try {
          setLoading(true);
          const response = await axios.get(`/api/requisiciones/${id}`);
          const { empresa_id, area, estado, detalles, impuestos: reqImpuestos } = response.data;
          setEmpresaId(empresa_id);
          setArea(area || '');
          setEstado(estado);
          // Extract moneda from first detalle
          if (detalles && detalles.length > 0 && detalles[0].moneda) {
            setMoneda(detalles[0].moneda);
          }
          // Add temporary unique IDs for the form's key prop, keep moneda for backend compatibility but remove from display
          setDetalles(detalles.map(d => ({
            ...d,
            id: uuidv4(),
            cantidad: d.cantidad != null ? String(d.cantidad) : '1',
            precio: d.precio != null ? String(d.precio) : '',
          })));
          // Load impuestos or use default
          if (reqImpuestos && reqImpuestos.length > 0) {
            setImpuestos(reqImpuestos.map(imp => ({ ...imp, id: uuidv4() })));
          }
          setError('');
        } catch (err) {
          setError('No se pudo cargar la requisición para editar.');
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchRequisicion();
    }
  }, [id, isEditMode]);

  // Fetch areas when empresa changes
  useEffect(() => {
    const fetchAreas = async () => {
      if (!empresaId) {
        setAreas([]);
        setArea('');
        return;
      }

      try {
        const response = await axios.get(`/api/areas/by_empresa/${empresaId}`);
        setAreas(response.data);

        // If the current area is not in the new list, reset it
        if (area && !response.data.find(a => a.codigo === area)) {
          setArea('');
        }
      } catch (err) {
        console.error('No se pudieron cargar las áreas:', err);
        setAreas([]);
      }
    };

    fetchAreas();
  }, [empresaId]);

  // Calculate totals
  const calculatedTotals = useMemo(() => {
    const subtotal = detalles.reduce((acc, d) => {
      const precio = parseFloat(d.precio) || 0;
      const cantidad = parseFloat(d.cantidad) || 0;
      return acc + (precio * cantidad);
    }, 0);

    // Use the monto from impuestos state directly (no auto-recalculation)
    const totalImpuestos = impuestos.reduce((acc, imp) => acc + (parseFloat(imp.monto) || 0), 0);
    const total = subtotal + totalImpuestos;

    return { subtotal, totalImpuestos, total };
  }, [detalles, impuestos]);

  // Sync editableTotal with calculated total (auto-update when detalles/impuestos change)
  useEffect(() => {
    setEditableTotal(calculatedTotals.total.toFixed(2));
  }, [calculatedTotals.total]);

  const handleDetalleChange = (detalleId, field, value) => {
    const newDetalles = detalles.map((d) => {
      if (d.id === detalleId) {
        return { ...d, [field]: value };
      }
      return d;
    });
    setDetalles(newDetalles);
  };

  const addDetalle = () => {
    setDetalles([
      ...detalles,
      { id: uuidv4(), articulo: '', cantidad: '1', unidad: '', precio: '' },
    ]);
  };

  const removeDetalle = (detalleId) => {
    setDetalles(detalles.filter((d) => d.id !== detalleId));
  };

  const handleImpuestoChange = (impuestoId, field, value) => {
    const subtotal = detalles.reduce((acc, d) => {
      const precio = parseFloat(d.precio) || 0;
      const cantidad = parseFloat(d.cantidad) || 0;
      return acc + (precio * cantidad);
    }, 0);

    const newImpuestos = impuestos.map((imp) => {
      if (imp.id === impuestoId) {
        const updatedImpuesto = { ...imp, [field]: value };

        // If porcentaje changes, auto-calculate monto (one-way) and round to 10 decimals
        if (field === 'porcentaje') {
          const calculated = subtotal * (parseFloat(value) / 100);
          updatedImpuesto.monto = Math.round(calculated * 10000000000) / 10000000000;
        }
        // If monto changes, don't touch porcentaje

        return updatedImpuesto;
      }
      return imp;
    });
    setImpuestos(newImpuestos);
  };

  const addImpuesto = () => {
    setImpuestos([
      ...impuestos,
      { id: uuidv4(), nombre: '', porcentaje: 0, monto: 0 },
    ]);
  };

  const removeImpuesto = (impuestoId) => {
    setImpuestos(impuestos.filter((imp) => imp.id !== impuestoId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const { subtotal, totalImpuestos } = calculatedTotals;
    const finalTotal = parseFloat(editableTotal) || 0;

    const cleanImpuestos = impuestos.map(({ id, ...rest }) => ({
      ...rest,
      porcentaje: parseFloat(rest.porcentaje) || 0,
      monto: parseFloat(rest.monto) || 0
    }));

    const requisicionData = {
      empresa_id: empresaId,
      area,
      estado,
      subtotal,
      iva: totalImpuestos,  // For backward compatibility
      total: finalTotal,
      impuestos: cleanImpuestos,
      // Remove temporary id before submitting, add moneda to each detalle
      detalles: detalles.map(({ id, ...rest }) => ({
        ...rest,
        unidad: rest.unidad || null,
        cantidad: rest.cantidad ? parseFloat(rest.cantidad) : null,
        precio: rest.precio ? parseFloat(rest.precio) : null,
        moneda: moneda, // Set global moneda on each detalle
      })),
    };

    try {
      if (isEditMode) {
        await axios.put(`/api/requisiciones/${id}`, requisicionData);
        navigate(`/requisiciones/${id}`);
      } else {
        await axios.post('/api/requisiciones', requisicionData);
        navigate('/requisiciones', { state: { refresh: true } });
      }
    } catch (err) {
      setError('No se pudo guardar la requisición. Verifique los datos.');
      console.error(err);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          {isEditMode ? 'Editar Requisición' : 'Crear Nueva Requisición'}
        </Typography>
        <form onSubmit={handleSubmit}>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Empresa</InputLabel>
            <Select value={empresaId} label="Empresa" onChange={(e) => setEmpresaId(e.target.value)}>
              {empresas.map((empresa) => (
                <MenuItem key={empresa.id} value={empresa.id}>
                  {empresa.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Área</InputLabel>
            <Select
              value={area}
              label="Área"
              onChange={(e) => setArea(e.target.value)}
              disabled={!empresaId || areas.length === 0}
            >
              {areas.map((areaItem) => (
                <MenuItem key={areaItem.id} value={areaItem.codigo}>
                  {areaItem.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isEditMode && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Estado</InputLabel>
              <Select value={estado} label="Estado" onChange={(e) => setEstado(e.target.value)}>
                <MenuItem value="Pendiente">Pendiente</MenuItem>
                <MenuItem value="Aprobada">Aprobada</MenuItem>
                <MenuItem value="Rechazada">Rechazada</MenuItem>
                <MenuItem value="En Cotización">En Cotización</MenuItem>
                <MenuItem value="Comprada">Comprada</MenuItem>
              </Select>
            </FormControl>
          )}

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Artículos
          </Typography>

          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              select
              label="Moneda"
              value={moneda}
              onChange={(e) => setMoneda(e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="MXN">MXN</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
            </TextField>
          </Box>

          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '40%' }}>Artículo</TableCell>
                  <TableCell>Cantidad</TableCell>
                  <TableCell>Unidad</TableCell>
                  <TableCell>Precio</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detalles.map((detalle) => (
                  <TableRow key={detalle.id}>
                    <TableCell>
                      <TextField
                        value={detalle.articulo}
                        onChange={(e) => handleDetalleChange(detalle.id, 'articulo', e.target.value)}
                        required
                        fullWidth
                        variant="standard"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={detalle.cantidad}
                        onChange={(e) => handleDetalleChange(detalle.id, 'cantidad', e.target.value)}
                        required
                        variant="standard"
                        inputProps={{ step: "0.0000000001", min: "0" }}
                        sx={{ width: '80px' }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={detalle.unidad}
                        onChange={(e) => handleDetalleChange(detalle.id, 'unidad', e.target.value)}
                        placeholder="ej. PZA"
                        variant="standard"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={detalle.precio}
                        onChange={(e) => handleDetalleChange(detalle.id, 'precio', e.target.value)}
                        InputProps={{
                          startAdornment: moneda === 'EUR' ? '€' : '$',
                        }}
                        variant="standard"
                        inputProps={{ step: "0.0000000001", min: "0" }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => removeDetalle(detalle.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button startIcon={<AddIcon />} onClick={addDetalle}>
            Añadir Artículo
          </Button>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>Impuestos</Typography>
          {impuestos.map((impuesto) => (
            <Box key={impuesto.id} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                label="Nombre del Impuesto"
                value={impuesto.nombre}
                onChange={(e) => handleImpuestoChange(impuesto.id, 'nombre', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Porcentaje (%)"
                type="number"
                value={impuesto.porcentaje}
                onChange={(e) => handleImpuestoChange(impuesto.id, 'porcentaje', e.target.value)}
                inputProps={{ step: "0.0000000001", min: "0" }}
                sx={{ width: 150 }}
              />
              <TextField
                label="Monto"
                type="number"
                value={impuesto.monto}
                onChange={(e) => handleImpuestoChange(impuesto.id, 'monto', e.target.value)}
                inputProps={{ step: "0.0000000001", min: "0" }}
                sx={{ width: 180 }}
              />
              <IconButton onClick={() => removeImpuesto(impuesto.id)} color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button startIcon={<AddIcon />} onClick={addImpuesto} sx={{ mt: 1 }}>Añadir Impuesto</Button>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6}></Grid>
              <Grid item xs={6}>
                <Typography align="right">Subtotal: ${calculatedTotals.subtotal.toFixed(2)}</Typography>
                {impuestos && impuestos.map((impuesto) => (
                  <Typography key={impuesto.id} align="right">
                    {impuesto.nombre} ({impuesto.porcentaje}%): ${(parseFloat(impuesto.monto) || 0).toFixed(2)}
                  </Typography>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Typography variant="h6">Total:</Typography>
                  <TextField
                    type="number"
                    value={editableTotal}
                    onChange={(e) => setEditableTotal(e.target.value)}
                    InputProps={{
                      startAdornment: '$',
                    }}
                    inputProps={{ step: "0.01", min: "0" }}
                    sx={{ width: '180px' }}
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Button type="submit" variant="contained" color="primary" sx={{ mt: 3, display: 'block' }}>
            {isEditMode ? 'Guardar Cambios' : 'Crear Requisición'}
          </Button>
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </form>
      </Paper>
    </Container>
  );
};

export default RequisicionForm;